/**
 * Luyện giao tiếp với AI (Gemini Live, plan 2026-10-03) — nút nổi + Drawer.
 *
 * Luồng: Bắt đầu → BE cấp token tạm → trình duyệt kết nối thẳng Gemini Live (mic 16kHz
 * PCM → AI trả audio 24kHz + lời thoại 2 chiều, ngắt lời được) → "Kết thúc & đánh giá"
 * → gộp audio 2 chiều thành 1 WAV → BE lưu + Gemini chấm → điểm + 5 tiêu chí + nhận xét.
 * Chỉ hiện ở môn ngôn ngữ (`speaking_language`).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Drawer,
  Empty,
  FloatButton,
  List,
  Progress,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AudioMutedOutlined,
  AudioOutlined,
  CommentOutlined,
  SoundOutlined,
  StopOutlined,
} from "@ant-design/icons";
import type { LiveServerMessage, Session } from "@google/genai";

import { ScoreTag, pctTo10 } from "@/components/ScoreTag";
import { SpeakingConsentForm } from "@/components/SpeakingConsentForm";
import { formatDateTime } from "@/lib/formatDate";
import {
  type ConversationSession,
  type TranscriptItem,
  createConversationSession,
  fetchConversationAudioUrl,
  getConversationSession,
  listConversationSessions,
  retryConversationEvaluation,
  submitConversationEvaluation,
} from "./api";
import { AiPlayer, MicStreamer, SAMPLE_RATE, mixToWav } from "./audio";

const { Text, Paragraph } = Typography;

const LANGUAGE_LABEL: Record<string, string> = {
  ja: "tiếng Nhật",
  en: "tiếng Anh",
  zh: "tiếng Trung",
  ko: "tiếng Hàn",
};

const CRITERIA_LABEL: Record<string, string> = {
  pronunciation: "Phát âm",
  fluency: "Trôi chảy",
  grammar: "Ngữ pháp",
  vocabulary: "Từ vựng",
  interaction: "Tương tác",
};

type Phase = "idle" | "consent" | "connecting" | "live" | "submitting" | "evaluating" | "result";

interface ErrResp {
  response?: { status?: number; data?: { detail?: string; code?: string } };
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

interface Props {
  courseSlug: string;
  language: string;
}

export function ConversationPracticeFab({ courseSlug, language }: Props) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptItem[]>([]);
  const [elapsed, setElapsed] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(300);
  const [muted, setMuted] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [result, setResult] = useState<ConversationSession | null>(null);
  const [history, setHistory] = useState<ConversationSession[]>([]);
  const [tab, setTab] = useState("practice");

  const sessionIdRef = useRef<number | null>(null);
  const liveRef = useRef<Session | null>(null);
  const micRef = useRef<MicStreamer | null>(null);
  const playerRef = useRef<AiPlayer | null>(null);
  const transcriptRef = useRef<TranscriptItem[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endingRef = useRef(false);

  const langLabel = LANGUAGE_LABEL[language] ?? language;

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await listConversationSessions(courseSlug));
    } catch {
      /* bỏ qua */
    }
  }, [courseSlug]);

  useEffect(() => {
    if (open) void loadHistory();
  }, [open, loadHistory]);

  const pushTranscript = (role: "user" | "ai", text: string) => {
    if (!text) return;
    const list = transcriptRef.current;
    const last = list[list.length - 1];
    const t = Math.round((micRef.current?.elapsed ?? 0) * 10) / 10;
    if (last && last.role === role) {
      last.text += text;
    } else {
      list.push({ role, text, t });
    }
    setTranscript([...list]);
  };

  const cleanupLive = async () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    try {
      liveRef.current?.close();
    } catch {
      /* đã đóng */
    }
    liveRef.current = null;
    await micRef.current?.stop();
    await playerRef.current?.close();
  };

  const onLiveMessage = (msg: LiveServerMessage) => {
    const sc = msg.serverContent;
    if (!sc) return;
    const now = micRef.current?.elapsed ?? 0;
    if (sc.interrupted) playerRef.current?.interrupt(now);
    for (const part of sc.modelTurn?.parts ?? []) {
      if (part.inlineData?.data) playerRef.current?.play(part.inlineData.data, now);
    }
    if (sc.inputTranscription?.text) pushTranscript("user", sc.inputTranscription.text);
    if (sc.outputTranscription?.text) pushTranscript("ai", sc.outputTranscription.text);
  };

  const start = async () => {
    setError(null);
    setResult(null);
    setTranscript([]);
    transcriptRef.current = [];
    endingRef.current = false;
    setElapsed(0);
    setPhase("connecting");
    let created;
    try {
      created = await createConversationSession(courseSlug);
    } catch (err) {
      const r = (err as ErrResp).response;
      if (r?.data?.code === "speaking_consent_required") {
        setPhase("consent");
        return;
      }
      setError(r?.data?.detail ?? "Không bắt đầu được phiên luyện.");
      setPhase("idle");
      return;
    }
    sessionIdRef.current = created.session.id;
    setMaxSeconds(created.max_seconds);

    try {
      const player = new AiPlayer();
      await player.resume();
      playerRef.current = player;
      // SDK nặng → chỉ tải khi bắt đầu phiên (không làm chậm trang môn/bài học).
      const { GoogleGenAI, Modality } = await import("@google/genai");
      const ai = new GoogleGenAI({
        apiKey: created.live.token,
        httpOptions: { apiVersion: "v1alpha" },
      });
      const session = await ai.live.connect({
        model: created.live.model,
        config: {
          responseModalities: [Modality.AUDIO],
          inputAudioTranscription: {},
          outputAudioTranscription: {},
        },
        callbacks: {
          onmessage: onLiveMessage,
          onerror: () => setError("Mất kết nối với AI."),
          onclose: () => {
            if (!endingRef.current) setError("Phiên hội thoại đã đóng.");
          },
        },
      });
      liveRef.current = session;
      const mic = new MicStreamer((b64) => {
        liveRef.current?.sendRealtimeInput({
          audio: { data: b64, mimeType: `audio/pcm;rate=${SAMPLE_RATE}` },
        });
      });
      micRef.current = mic;
      await mic.start();
      // AI chào trước (system prompt yêu cầu mở lời).
      session.sendRealtimeInput({ text: "Hello" });
      setPhase("live");
      tickRef.current = setInterval(() => {
        const e = micRef.current?.elapsed ?? 0;
        setElapsed(e);
        setAiSpeaking(playerRef.current?.speaking ?? false);
      }, 250);
    } catch {
      await cleanupLive();
      setError("Không kết nối được micro hoặc AI. Kiểm tra quyền micro rồi thử lại.");
      setPhase("idle");
    }
  };

  const pollResult = async (id: number) => {
    for (let i = 0; i < 60; i++) {
      await sleep(3000);
      try {
        const s = await getConversationSession(id);
        if (s.status === "evaluated" || s.status === "failed") {
          setResult(s);
          setPhase("result");
          void loadHistory();
          return;
        }
      } catch {
        /* thử lại */
      }
    }
    setError("Đánh giá lâu hơn dự kiến — xem lại ở tab Lịch sử sau ít phút.");
    setPhase("idle");
  };

  const finish = async () => {
    if (endingRef.current) return;
    endingRef.current = true;
    const id = sessionIdRef.current;
    const mic = micRef.current;
    const player = playerRef.current;
    const duration = mic?.elapsed ?? 0;
    const studentPcm = mic?.recorded ?? [];
    const aiSegments = player ? [...player.segments] : [];
    await cleanupLive();
    if (!id || duration < 3) {
      setPhase("idle");
      setError("Phiên quá ngắn để đánh giá.");
      return;
    }
    setPhase("submitting");
    try {
      const wav = mixToWav(studentPcm, aiSegments);
      await submitConversationEvaluation(id, wav, transcriptRef.current, duration);
      setPhase("evaluating");
      await pollResult(id);
    } catch {
      setError("Không gửi được bản ghi để đánh giá.");
      setPhase("idle");
    }
  };

  // Hết giờ → tự kết thúc + đánh giá.
  useEffect(() => {
    if (phase === "live" && elapsed >= maxSeconds) void finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, elapsed, maxSeconds]);

  // Đóng Drawer / rời trang giữa phiên → ngắt kết nối (không đánh giá).
  useEffect(
    () => () => {
      void cleanupLive();
    },
    [],
  );

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    micRef.current?.setMuted(next);
  };

  const retry = async (id: number) => {
    try {
      await retryConversationEvaluation(id);
      setPhase("evaluating");
      await pollResult(id);
    } catch {
      message.error("Không gửi lại được, thử lại sau.");
    }
  };

  const closeDrawer = () => {
    if (phase === "live") {
      endingRef.current = true;
      void cleanupLive();
      setPhase("idle");
    }
    setOpen(false);
  };

  const practicePane = (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {error && (
        <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />
      )}

      {phase === "consent" && (
        <SpeakingConsentForm grantLabel="Đồng ý và bắt đầu" onGranted={() => void start()} />
      )}

      {phase === "idle" && (
        <Card size="small">
          <Paragraph>
            Trò chuyện bằng giọng nói với AI bằng <Text strong>{langLabel}</Text> về chủ đề tự do.
            AI nói chậm, câu ngắn, bạn có thể ngắt lời bất cứ lúc nào. Mỗi phiên tối đa{" "}
            {Math.round(maxSeconds / 60)} phút; bấm <Text strong>Kết thúc &amp; đánh giá</Text> để
            AI chấm phát âm, độ trôi chảy, ngữ pháp, từ vựng và khả năng tương tác.
          </Paragraph>
          <Text type="secondary">Nên dùng tai nghe để AI không nghe lại chính giọng của nó.</Text>
          <div style={{ marginTop: 12 }}>
            <Button type="primary" icon={<AudioOutlined />} onClick={() => void start()}>
              Bắt đầu hội thoại
            </Button>
          </div>
        </Card>
      )}

      {phase === "connecting" && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
          <div style={{ marginTop: 8 }}>Đang kết nối AI…</div>
        </div>
      )}

      {phase === "live" && (
        <Card size="small">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space style={{ justifyContent: "space-between", width: "100%" }}>
              <Tag color={aiSpeaking ? "purple" : "green"} icon={<SoundOutlined />}>
                {aiSpeaking ? "AI đang nói…" : muted ? "Đã tắt mic" : "Đang nghe bạn…"}
              </Tag>
              <Text strong>
                {fmt(elapsed)} / {fmt(maxSeconds)}
              </Text>
            </Space>
            <Progress percent={Math.min(100, (elapsed / maxSeconds) * 100)} showInfo={false} />
            <Space wrap>
              <Button
                icon={muted ? <AudioMutedOutlined /> : <AudioOutlined />}
                onClick={toggleMute}
              >
                {muted ? "Bật mic" : "Tắt mic"}
              </Button>
              <Button type="primary" danger icon={<StopOutlined />} onClick={() => void finish()}>
                Kết thúc &amp; đánh giá
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      {(phase === "submitting" || phase === "evaluating") && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
          <div style={{ marginTop: 8 }}>
            {phase === "submitting" ? "Đang gửi bản ghi…" : "AI đang đánh giá cuộc hội thoại…"}
          </div>
        </div>
      )}

      {phase === "result" && result && (
        <SessionResult session={result} onRetry={() => void retry(result.id)} />
      )}

      {phase === "result" && <Button onClick={() => setPhase("idle")}>Luyện phiên mới</Button>}

      {transcript.length > 0 && phase !== "result" && <TranscriptView items={transcript} />}
    </Space>
  );

  const historyPane =
    history.length === 0 ? (
      <Empty description="Chưa có phiên luyện nào" />
    ) : (
      <Collapse
        items={history.map((s) => ({
          key: s.id,
          label: (
            <Space>
              <Text>{formatDateTime(s.started_at)}</Text>
              <Text type="secondary">{fmt(s.duration_seconds)}</Text>
              {s.status === "evaluated" && s.score !== null ? (
                <ScoreTag value={pctTo10(s.score)} />
              ) : (
                <Tag>{s.status === "failed" ? "Chấm lỗi" : "Đang chấm"}</Tag>
              )}
            </Space>
          ),
          children: <SessionResult session={s} onRetry={() => void retry(s.id)} />,
        }))}
      />
    );

  return (
    <>
      <FloatButton
        icon={<CommentOutlined />}
        type="primary"
        tooltip={`Luyện giao tiếp ${langLabel} với AI`}
        onClick={() => setOpen(true)}
        style={{ right: 24, bottom: 88, background: "#13a8a8" }}
        data-testid="conversation-practice-fab"
      />
      <Drawer
        open={open}
        onClose={closeDrawer}
        width={460}
        title={`Luyện giao tiếp ${langLabel} với AI`}
        destroyOnClose={false}
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: "practice", label: "Luyện", children: practicePane },
            { key: "history", label: "Lịch sử", children: historyPane },
          ]}
        />
      </Drawer>
    </>
  );
}

function TranscriptView({ items }: { items: TranscriptItem[] }) {
  return (
    <List
      size="small"
      bordered
      dataSource={items}
      renderItem={(it) => (
        <List.Item style={{ justifyContent: it.role === "user" ? "flex-end" : "flex-start" }}>
          <div style={{ maxWidth: "85%", textAlign: it.role === "user" ? "right" : "left" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {it.role === "user" ? "Bạn" : "AI"}
            </Text>
            <div>{it.text}</div>
          </div>
        </List.Item>
      )}
    />
  );
}

function SessionResult({
  session,
  onRetry,
}: {
  session: ConversationSession;
  onRetry: () => void;
}) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    },
    [audioUrl],
  );

  const play = async () => {
    try {
      setAudioUrl(await fetchConversationAudioUrl(session.id));
    } catch {
      message.error("Không tải được bản ghi.");
    }
  };

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {session.status === "failed" && (
        <Alert
          type="warning"
          showIcon
          message={session.error_message || "AI chưa đánh giá được phiên này."}
          action={
            <Button size="small" onClick={onRetry}>
              Đánh giá lại
            </Button>
          }
        />
      )}
      {session.status === "evaluated" && session.score !== null && (
        <>
          <Space>
            <Text strong>Điểm tổng:</Text>
            <ScoreTag value={pctTo10(session.score)} />
          </Space>
          <Space wrap>
            {Object.entries(CRITERIA_LABEL).map(([k, label]) => {
              const v = session.criteria[k as keyof typeof session.criteria];
              return v === undefined ? null : (
                <Space key={k} size={4}>
                  <Text type="secondary">{label}</Text>
                  <ScoreTag value={pctTo10(v)} />
                </Space>
              );
            })}
          </Space>
          {session.feedback && (
            <Paragraph style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
              {session.feedback}
            </Paragraph>
          )}
        </>
      )}
      {session.has_audio &&
        (audioUrl ? (
          <audio controls src={audioUrl} style={{ width: "100%" }} />
        ) : (
          <Button size="small" icon={<SoundOutlined />} onClick={() => void play()}>
            Nghe lại cuộc hội thoại
          </Button>
        ))}
      {session.transcript.length > 0 && (
        <Collapse
          size="small"
          items={[
            {
              key: "t",
              label: "Lời thoại",
              children: <TranscriptView items={session.transcript} />,
            },
          ]}
        />
      )}
    </Space>
  );
}
